import { useState, useMemo } from 'react';

export const useGroupSelection = (groups = []) => {
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  // Handle selection of individual group
  const handleSelectGroup = (chatId, checked) => {
    const newSelected = new Set(selectedGroups);
    if (checked) {
      newSelected.add(chatId);
    } else {
      newSelected.delete(chatId);
    }
    setSelectedGroups(newSelected);
  };

  // Handle select all/none
  const handleSelectAll = (checked, groupsToSelect = groups) => {
    if (checked) {
      setSelectedGroups(new Set(groupsToSelect.map(g => g.chat_id)));
    } else {
      setSelectedGroups(new Set());
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedGroups(new Set());
  };

  // Select groups by filter criteria
  const selectByFilter = (filterFn) => {
    const filteredGroups = groups.filter(filterFn);
    setSelectedGroups(new Set(filteredGroups.map(g => g.chat_id)));
  };

  // Select groups by status
  const selectByStatus = (statusField, statusValue) => {
    const filteredGroups = groups.filter(group => group[statusField] === statusValue);
    setSelectedGroups(new Set(filteredGroups.map(g => g.chat_id)));
  };

  // Toggle selection for a group
  const toggleGroupSelection = (chatId) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(chatId)) {
      newSelected.delete(chatId);
    } else {
      newSelected.add(chatId);
    }
    setSelectedGroups(newSelected);
  };

  // Get selected groups data
  const getSelectedGroupsData = () => {
    return groups.filter(group => selectedGroups.has(group.chat_id));
  };

  // Check if group is selected
  const isGroupSelected = (chatId) => {
    return selectedGroups.has(chatId);
  };

  // Check if all visible groups are selected
  const areAllSelected = (visibleGroups = groups) => {
    if (visibleGroups.length === 0) return false;
    return visibleGroups.every(group => selectedGroups.has(group.chat_id));
  };

  // Check if some groups are selected (for indeterminate state)
  const areSomeSelected = (visibleGroups = groups) => {
    return visibleGroups.some(group => selectedGroups.has(group.chat_id));
  };

  // Selection statistics
  const selectionStats = useMemo(() => {
    const selectedGroupsData = getSelectedGroupsData();
    
    return {
      totalSelected: selectedGroups.size,
      selectedByStatus: {
        subscribed: selectedGroupsData.filter(g => g.subscription_status === 'subscribed').length,
        pending: selectedGroupsData.filter(g => g.subscription_status === 'pending').length,
        analyzed: selectedGroupsData.filter(g => g.ai_analysis_status === 'completed').length,
        notAnalyzed: selectedGroupsData.filter(g => g.ai_analysis_status !== 'completed').length,
        highQuality: selectedGroupsData.filter(g => g.quality >= 70).length,
        lowQuality: selectedGroupsData.filter(g => g.quality > 0 && g.quality < 70).length
      },
      totalMembers: selectedGroupsData.reduce((sum, group) => sum + (group.member_count || 0), 0),
      averageQuality: selectedGroupsData.length > 0 
        ? selectedGroupsData
            .filter(g => g.quality > 0)
            .reduce((sum, group) => sum + (group.quality || 0), 0) / 
          selectedGroupsData.filter(g => g.quality > 0).length
        : 0
    };
  }, [selectedGroups, groups]);

  // Quick selection helpers
  const quickSelections = {
    // Select all high quality groups
    selectHighQuality: () => selectByFilter(group => group.quality >= 70),
    
    // Select all groups pending analysis
    selectPendingAnalysis: () => selectByStatus('ai_analysis_status', 'pending'),
    
    // Select all subscribed groups
    selectSubscribed: () => selectByStatus('subscription_status', 'subscribed'),
    
    // Select all groups with basic info completed
    selectWithBasicInfo: () => selectByStatus('basic_info_status', 'completed'),
    
    // Select large groups (>1000 members)
    selectLargeGroups: () => selectByFilter(group => (group.member_count || 0) > 1000),
    
    // Select active groups (>1 post per hour)
    selectActiveGroups: () => selectByFilter(group => (group.post_per_hour || 0) > 1),
    
    // Select uncategorized groups
    selectUncategorized: () => selectByFilter(group => !group.category || group.category === 'Uncategorized')
  };

  return {
    // State
    selectedGroups,
    
    // Actions
    handleSelectGroup,
    handleSelectAll,
    clearSelection,
    selectByFilter,
    selectByStatus,
    toggleGroupSelection,
    setSelectedGroups,
    
    // Getters
    getSelectedGroupsData,
    isGroupSelected,
    areAllSelected,
    areSomeSelected,
    
    // Stats
    selectionStats,
    
    // Quick selections
    quickSelections,
    
    // Computed values
    selectedCount: selectedGroups.size,
    hasSelection: selectedGroups.size > 0,
    selectedArray: Array.from(selectedGroups)
  };
};
